import NextLink from "next/link";
import { Popover } from "@base-ui/react/popover";
import { Button } from "@/components/frontend/ui/button";
import type { CardVariant } from "@/components/frontend/ui/card";
import Card, { CardContent } from "@/components/frontend/ui/card";
import type { Partner } from "@/lib/types/marketsuite/datatypes/partner";
import { buildUrl } from "@/lib/utils/url";
import { Globe, Mail, Phone } from "lucide-react";
import { useTranslations } from "next-intl";
import { usePartnerOverview } from "./context";
import { visibleChildPartnerTags } from "./constants";

interface PartnerCardProps {
	partner: Partner;
	variant?: CardVariant;
	childVariant?: CardVariant;
}

export default function PartnerCard({ partner, variant, childVariant }: PartnerCardProps) {
	const { detailUrlBase } = usePartnerOverview();
	const t = useTranslations("ms.partner-overview");
	const address = [partner.address.street, partner.address.house_number, partner.address.postal_code, partner.address.location].filter(Boolean).join(", ");
	const visibleMembers = partner.members.filter((member) => member.tags.some((tag) => visibleChildPartnerTags.includes(tag)));

	return (
		<article className="h-full">
			<NextLink href={buildUrl(detailUrlBase ?? "", partner.slug)} className="h-full">
				<Card hover className="h-full" variant={variant}>
					<CardContent>
						<p className="h4 text-lg font-bold">{partner.name}</p>
						{address && <p className="text-sm">{address}</p>}
						<div className="flex flex-col gap-1 grow">
							{partner.phone && (
								<span className="flex items-center gap-1 text-xs">
									<Phone className="size-3" /> {partner.phone}
								</span>
							)}

							{partner.email && (
								<span className="flex items-center gap-1 text-xs">
									<Mail className="size-3" /> {partner.email}
								</span>
							)}

							{partner.website_link && (
								<span className="flex items-center gap-1 text-xs">
									<Globe className="size-3" /> {partner.website_link}
								</span>
							)}
						</div>

						<Button>{t("view-availability")}</Button>

						{visibleMembers.length > 0 && (
							<Popover.Root>
								<Popover.Trigger
									render={
										<Button
											variant="subtle"
											className="w-full"
											onClick={(event) => {
												event.preventDefault();
												event.stopPropagation();
											}}
										/>
									}
								>
									{t("view-partners")}
								</Popover.Trigger>

								<Popover.Portal>
									<Popover.Positioner sideOffset={8} align="start" className="z-1005">
										<Popover.Popup className="max-h-64 overflow-y-auto transition-all duration-150 data-ending-style:scale-95 data-starting-style:opacity-0">
											<Card variant={childVariant}>
												<CardContent>
													<ul className="flex flex-col gap-3">
														{visibleMembers.map((member) => {
															const visibleTag = member.tags.find((tag) => visibleChildPartnerTags.includes(tag));

															return (
																<li key={member.id}>
																	<p className="text-sm font-bold">
																		{member.name}{" "}
																		{visibleTag && <span className="font-normal text-xs">({visibleTag})</span>}
																	</p>

																	{member.email && (
																		<a
																			href={`mailto:${member.email}`}
																			className="flex items-center gap-1 text-xs whitespace-nowrap"
																		>
																			<Mail className="size-3" /> {member.email}
																		</a>
																	)}
																</li>
															);
														})}
													</ul>
												</CardContent>
											</Card>
										</Popover.Popup>
									</Popover.Positioner>
								</Popover.Portal>
							</Popover.Root>
						)}
					</CardContent>
				</Card>
			</NextLink>
		</article>
	);
}
